import { Component, ErrorInfo, ReactNode } from "react";

import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";
import { faHome } from "@fortawesome/pro-solid-svg-icons";

import { EmptyState } from "src/design-system/EmptyState";
import {
  tracker,
  trackingEvents,
} from "src/instrumentation/customTrackingEvents";
import { parseUrl } from "src/router/urls";
import { logger } from "src/utils/logger";

type FallbackRender = (props: { resetErrorBoundary: () => void }) => ReactNode;

type Props = {
  children: ReactNode;
  fallbackErrorContent?: ReactNode | FallbackRender;
};

export const instrumentError = (error: Error, errorInfo?: ErrorInfo) => {
  const { orgId } = parseUrl(window.location.href);
  tracker.emit(
    trackingEvents.clientRuntimeError({
      error_message: error.message ?? "",
      error_stack: error.stack ?? "",
      error_component_stack: errorInfo?.componentStack ?? "",
      organization_id: orgId,
    }),
  );
};

export class ErrorBoundary extends Component<Props, { hasError: boolean }> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error("Error boundary caught an error", {
      // Done because we want this stringified, but simply stringifying and error gives an empty object.
      // https://stackoverflow.com/questions/18391212/is-it-not-possible-to-stringify-an-error-using-json-stringify
      error: JSON.parse(
        JSON.stringify(error, Object.getOwnPropertyNames(error)),
      ),
      errorInfo,
    });
    instrumentError(error, errorInfo);
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      const { fallbackErrorContent } = this.props;

      if (typeof fallbackErrorContent === "function") {
        return fallbackErrorContent({
          resetErrorBoundary: this.resetErrorBoundary,
        });
      }

      return (
        fallbackErrorContent || (
          <div className="flex h-full items-center justify-center">
            <EmptyState
              actionButton={{
                text: "Go to home page",
                icon: faHome,
                onClick: () => window.location.assign("/"),
              }}
              dataLoc="error-boundary"
              description="Oops, something went wrong."
              headline="Error"
              icon={faExclamationCircle}
              variant="error"
            />
          </div>
        )
      );
    }

    return this.props.children;
  }
}
